/**
 * Prime camera + microphone permissions INSIDE a user-gesture handler
 * (button onClick) BEFORE any async work that breaks the gesture chain.
 *
 * Why: iOS Safari (and to a lesser extent Android Chrome) only show the
 * mic / camera permission prompt when `getUserMedia` is invoked
 * synchronously inside a user gesture. Plyx's live flow used to:
 *   1. User taps "Start broadcasting"
 *   2. await api.post('/plyx/live/start')   ← async network call BREAKS gesture
 *   3. <LiveKitRoom video audio /> mounts → calls getUserMedia → iOS silently denies
 *
 * This helper restores the gesture context by requesting permission
 * IMMEDIATELY on click, then releasing the tracks so LiveKit can grab
 * fresh ones with the same permission grant.
 *
 * Returns an object { ok: true } on success, or { ok: false, error }
 * with a user-friendly reason string ("denied", "no_device",
 * "not_supported", "unknown"). Never throws — callers should toast
 * and continue or abort based on `ok`.
 */
export async function primePlyxMediaPermissions({ video = true, audio = true } = {}) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { ok: false, error: "not_supported" };
  }
  let stream = null;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video, audio });
    // Release the temporary tracks immediately — LiveKit will create
    // its own with the permission grant we just secured.
    stream.getTracks().forEach((t) => {
      try { t.stop(); } catch { /* ignore */ }
    });
    return { ok: true };
  } catch (err) {
    const name = err?.name || "";
    let reason = "unknown";
    if (name === "NotAllowedError" || name === "PermissionDeniedError") reason = "denied";
    else if (name === "NotFoundError" || name === "DevicesNotFoundError") reason = "no_device";
    else if (name === "NotReadableError") reason = "busy";
    else if (name === "SecurityError") reason = "insecure";
    return { ok: false, error: reason, raw: err?.message || String(err) };
  }
}

/** Translate a `primePlyxMediaPermissions` error code into a toast message. */
export function plyxMediaPermissionMessage(error) {
  switch (error) {
    case "denied":
      return "Plyx needs camera + mic permission to go live. Tap the lock icon in your browser bar and allow them, then try again.";
    case "no_device":
      return "We couldn't find a camera or microphone on this device.";
    case "busy":
      return "Your camera or mic is in use by another app. Close it and try again.";
    case "insecure":
      return "Live streaming requires HTTPS. Open Plyx in a secure tab.";
    case "not_supported":
      return "This browser doesn't support live streaming. Try Chrome or Safari.";
    default:
      return "Couldn't access your camera + mic. Refresh the page and try again.";
  }
}
